// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartPageTemplateBreakpoint } from "@flip/App/Components/Cart/CartPageTemplate";
import { constants } from "@flip/XDL/constants";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { xScreen } from "@flip/XDL/Utilities";
import { mergeWithCss } from "@flip/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    leftTitle?: string;
    rightTitles?: string[];
    extendedStyles?: LinesHeaderStyles;
    orderLineStyle?: boolean;
    isFullWidth?: boolean;
}

export interface LinesHeaderStyles {
    wrapper?: InjectableCss;
    itemDescription?: InjectableCss;
    rightSection?: InjectableCss;
    qty?: InjectableCss;
    priceUnit?: InjectableCss;
    total?: InjectableCss;
    actions?: InjectableCss;
}

export const linesHeaderStyles: LinesHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            color: ${getColor("common.white")};
            padding: 12px;
            background: ${getColor("common.ui9")};
            font-size: 12px;
            line-height: 14px;
            font-weight: ${fontConfig.weight.semiBold};
            width: 100%;

            ${xScreen("lte", cartPageTemplateBreakpoint)} {
                display: none;
            }
            /* XNG-Changes:FLIP-938 */
            @media print {
                display: none;
            }
        `,
    },
    itemDescription: {
        css: css`
            width: 450px;
            margin-right: 24px;
            &.orderLineStyle {
                width: 810px;
            }

            ${({ isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    flex: 1;
                `};
            `}
        `,
    },
    rightSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            ${({ isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    min-width: 478px;
                    flex: 0;
                `};
            `}

            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                display: none;
            }
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
        `,
    },
    priceUnit: {
        css: css`
            flex: 1;
        `,
    },
    total: {
        css: css`
            flex: 1;
        `,
    },
    actions: {
        css: css`
            padding: 0 24px;
        `,
    },
};

const LinesHeader = ({ leftTitle, rightTitles, orderLineStyle, extendedStyles, isFullWidth }: OwnProps) => {
    const [styles] = useState(() => mergeWithCss(linesHeaderStyles, extendedStyles));
    const orderLineStyleClassName = orderLineStyle ? "orderLineStyle" : "";

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.itemDescription} className={orderLineStyleClassName} {...{ isFullWidth }}>
                <Typography>{translate(leftTitle || "")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection} {...{ isFullWidth }}>
                <StyledWrapper {...styles.priceUnit}>{translate(rightTitles?.[0] || "")}</StyledWrapper>
                <StyledWrapper {...styles.qty}>{translate(rightTitles?.[1] || "")}</StyledWrapper>
                <StyledWrapper {...styles.total}>{translate(rightTitles?.[2] || "")}</StyledWrapper>
                <StyledWrapper {...styles.actions}></StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

LinesHeader.defaultProps = {
    leftTitle: translate("Item Description"),
    rightTitles: ["Price/Unit", "QTY", "Total"],
};

export default LinesHeader;
